<template>
  <Body class="page-checklist" />

  <section class="section page-header">
    <div class="wrap">
      <div class="text">
        <div class="theme" v-html="$t('평화를부르는어머니사랑의언어')" />
        <h1 class="title" v-html="$t('데일리spanclasscolor체크span')" />
        <div
          class="desc"
          v-html="$t('오늘하루어머니사랑의언어로어떤평화를이루셨나요br7가지중실천한항목에체크해주세요')"
        />
      </div>
    </div>
  </section>

  <!-- Check -->
  <section class="section checklist">
    <div class="inner">
      <!-- List -->
      <div class="wrap daily">
        <div class="today">
          <div class="date">
            {{ formattedDate }}
          </div>
        </div>
        <CheckList
          :checked-items="checkedItems" :date="today" @on-item-click="onDailyCheckItemClick"
        />
      </div>

      <!-- Calendar -->
      <div class="monthly">
        <div class="wrap calendar">
          <ChecklistCalendar :stats="myCalendarStatistics ?? []" @date-click="onDateClick" @on-month-change="onMonthChange" />
          <!-- 팝업이 열릴 때 body에 lock 클래스를 붙여주시고,
          팝업 닫을 때 클래스 제거해주세요 -->
          <div class="clandar-legend">
            <div class="part">
              {{ $t('일부실천') }}
            </div>
            <div class="all">
              {{ $t('모두실천') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- My Data -->
  <ChecklistMyStatistics
    v-if="myStatistics"
    :my-statistics="myStatistics"
    :type="statisticsType"
    @on-tab-click="onTabClick"
  />

  <!-- popup : popcheck -->
  <ChecklistCalendarPopup
    v-if="isShowCalendarPopup"
    :date="selectedDate"
    @close="isShowCalendarPopup = false"
    @on-item-click="onDailyCheckItemClick"
  />

  <!-- alert -->
  <!-- <div class="alert-box">저장되었어요.</div> -->
</template>

<script lang="ts" setup>
export interface HmlCampainItemModel {
  id: number
  theme: string
  title: string
  description: string
  campaignId: number
}

const { $t } = useNuxtApp()

// Head,Body
useHead({
  title: `${$t('데일리체크')} - ${$t('어머니사랑과평화의날캠페인')}`,
  link: [{ rel: 'stylesheet', href: `/css/checklist.css?${useBuildTime()}` }],
})

const { showSnackbar } = useSnackbar()
const { showLoginPopup, isLoggedIn } = useAuth()
const { fetchMyCampaignStatistics, fetchMyCampaignStatisticsCalendar, fetchMyCheckedItems, toggleItem } = useChecklist()

const today = new Date()
const statisticsType = ref<'month' | 'total'>('month')

const yearMonth = ref(new Date().toISOString().slice(0, 7))

// 오늘 체크 항목 데이터 불러오기.
const { data: checkedItemsRef, refresh: refreshTodayCheckedItems } = await useAsyncData('checkedItems', () => fetchMyCheckedItems({
  date: today,
}))

const checkedItems = computed(() => {
  return checkedItemsRef.value ?? {}
})

// 캘린더 데이터 불러오기.
const { data: myCalendarStatistics, refresh: refreshCalendarData } = await useAsyncData('calendarData', () => fetchMyCampaignStatisticsCalendar(yearMonth.value), {
  watch: [yearMonth],
})

// 참여 통계 데이터 불러오기.
const { data: myStatistics, refresh: refreshMyCampaignStats } = await useAsyncData(
  'myCampaignStats',
  () => fetchMyCampaignStatistics(statisticsType.value === 'month' ? yearMonth.value : undefined),
  {
    watch: [statisticsType],
  },
)
// popup
const isShowCalendarPopup = ref(false)

const selectedDate = ref(new Date())

const { locale } = useI18n()

const formattedDate = formatIntlDate(today, locale.value)

async function onDateClick(info: { date: Date }) {
  const now = new Date()
  const $7daysAgo = new Date()
  $7daysAgo.setDate($7daysAgo.getDate() - 7)

  if (info.date < $7daysAgo) {
    alert($t('{}일이전의날짜는선택할수없습니다').replace('{}', '7'))
    return
  }

  if (info.date > now) {
    alert($t('미래의날짜는선택할수없습니다'))
    return
  }
  
  if (!isLoggedIn.value) {
    isShowCalendarPopup.value = false
    showLoginPopup()
    return
  }
  
  isShowCalendarPopup.value = true

  selectedDate.value = info.date
}

async function onDailyCheckItemClick(campaignItemId: number, date: Date, callback?: () => Promise<void>) {
  if (!isLoggedIn.value) {
    isShowCalendarPopup.value = false
    showLoginPopup()
    return
  }

  await toggleItem({
    campaignItemId,
    date,
  })

  await refreshMyCampaignStats()
  await refreshCalendarData()

  if (callback) {
    await callback()
  }

  const todayString = formatYYYYmmdd(today)
  const dateString = formatYYYYmmdd(date)
  if (dateString === todayString) {
    await refreshTodayCheckedItems()
  }

  showSnackbar($t('저장되었어요'))
}

async function onTabClick(info: { type: 'month' | 'total' }) {
  statisticsType.value = info.type
}

async function onMonthChange(info: { yearMonth: string }) {
  yearMonth.value = info.yearMonth
}
</script>
